import { useId, type ComponentProps } from "react";
import { cn } from "./cn";

// Employee avatars, drawn on a 40 unit grid. The hue lives only here: chrome never borrows an
// employee colour. Built to hold up at 20px on a 1x screen: features are at least 2 units
// (1px at 20px) thick, the body gets a soft inner shade and a dark rim so its edge reads on
// the dark ground, and the eyes carry catchlights so the face reads before the colour does.

export const blobHues = ["teal", "amber", "violet", "blue", "orange", "pink", "green"] as const;
export type BlobHue = (typeof blobHues)[number];

export const blobFaces = ["plain", "glasses", "brow", "wink", "happy", "shades"] as const;
export type BlobFace = (typeof blobFaces)[number];

interface Palette {
  readonly light: string;
  readonly mid: string;
  readonly deep: string;
  readonly ink: string;
  readonly body: string;
}

const palettes: Record<BlobHue, Palette> = {
  teal: {
    light: "#BDF2FF",
    mid: "#29C5E6",
    deep: "#1686A3",
    ink: "#032A33",
    body: "M20 3.2c7.6-.2 14.8 4.4 16.4 11.6 1.6 7.1-1.5 15.6-7.9 19.4-6.5 3.9-15.7 3-20.6-2.2C3.2 27.3 2.2 19.2 5 12.9 7.7 6.8 13.3 3.4 20 3.2Z",
  },
  amber: {
    light: "#FFE6B0",
    mid: "#FFC24B",
    deep: "#C98A1A",
    ink: "#3A2606",
    body: "M21 3c8 .3 14.6 5 15.8 12.3 1.1 7.3-2.2 15.2-8.7 18.9-6.2 3.5-14.7 2.6-19.6-2.4C3.7 26.9 2.4 19.5 5.2 13.2 7.9 7 13.8 2.7 21 3Z",
  },
  violet: {
    light: "#E2D4FF",
    mid: "#9D6CFF",
    deep: "#5E36C9",
    ink: "#1B0B45",
    body: "M19.5 2.8c8.3-.4 15.5 5.2 16.7 12.7 1.2 7.2-2.8 14.7-9.3 18.1-6.4 3.4-14.2 2.5-18.8-2.7C3.6 26 2.6 18.2 5.6 12.3 8.3 6.8 13.2 3.1 19.5 2.8Z",
  },
  blue: {
    light: "#CADDFF",
    mid: "#4D8DFF",
    deep: "#2556C4",
    ink: "#0A1A3D",
    body: "M20 2.9c8 0 14.9 4.9 16.3 12.2 1.4 7.2-2 15.3-8.6 18.9-6.4 3.5-14.9 2.6-19.6-2.5C3.4 26.5 2.5 18.9 5.3 12.9 8 7 13.3 2.9 20 2.9Z",
  },
  orange: {
    light: "#FFDCC6",
    mid: "#FF9C66",
    deep: "#D2622E",
    ink: "#3A1506",
    body: "M20.8 3.1c7.7.4 14.2 5.4 15.5 12.4 1.3 7-2.1 14.6-8.3 18.3-6.4 3.8-15 3.1-19.9-1.9C3.4 27 2.5 19.6 5.1 13.6 7.8 7.3 13.9 2.8 20.8 3.1Z",
  },
  pink: {
    light: "#FFCDF2",
    mid: "#F062D0",
    deep: "#A8308C",
    ink: "#3D0A33",
    body: "M20.5 3c7.9.1 14.3 4.7 15.9 11.8 1.5 6.9-1.4 15-7.7 18.8-6.4 3.8-15.4 3.2-20.3-1.8C3.5 26.9 2.4 19.4 5 13.4 7.6 7.2 13.5 2.9 20.5 3Z",
  },
  // Not drawn in Paper yet: matched to the family's brightness and saturation.
  green: {
    light: "#D2F7C9",
    mid: "#6FD66A",
    deep: "#2F9447",
    ink: "#08301A",
    body: "M20.3 3c7.8.2 14.6 5 15.9 12.1 1.3 7.1-1.9 15-8.2 18.7-6.3 3.7-15.1 3-20-2C3.5 27 2.5 19.4 5.2 13.3 7.9 7.1 13.4 2.8 20.3 3Z",
  },
};

// Eye centres. Wide set and a touch low reads friendlier than centred.
const L = { x: 14.6, y: 21 };
const R = { x: 25.4, y: 21 };

const Eye = ({ x, y, ink }: { x: number; y: number; ink: string }) => (
  <>
    <ellipse cx={x} cy={y} rx="2.5" ry="3.1" fill={ink} />
    <circle cx={x + 0.85} cy={y - 1.15} r="0.95" fill="#fff" fillOpacity="0.9" />
  </>
);

const Closed = ({ x, y, ink, up = true }: { x: number; y: number; ink: string; up?: boolean }) => (
  <path
    d={up ? `M${x - 2.6} ${y + 0.9}q2.6 -3.4 5.2 0` : `M${x - 2.6} ${y - 0.4}q2.6 2.6 5.2 0`}
    fill="none"
    stroke={ink}
    strokeWidth="2.1"
    strokeLinecap="round"
  />
);

const Face = ({ face, ink }: { face: BlobFace; ink: string }) => {
  switch (face) {
    case "plain":
      return (
        <>
          <Eye {...L} ink={ink} />
          <Eye {...R} ink={ink} />
        </>
      );
    case "glasses":
      return (
        <>
          <circle cx={L.x} cy={L.y} r="4.3" fill="#fff" fillOpacity="0.22" stroke={ink} strokeWidth="1.9" />
          <circle cx={R.x} cy={R.y} r="4.3" fill="#fff" fillOpacity="0.22" stroke={ink} strokeWidth="1.9" />
          <path d={`M${L.x + 4.3} ${L.y - 0.6}h${R.x - L.x - 8.6}`} stroke={ink} strokeWidth="1.9" strokeLinecap="round" />
          <circle cx={L.x} cy={L.y + 0.2} r="1.7" fill={ink} />
          <circle cx={R.x} cy={R.y + 0.2} r="1.7" fill={ink} />
        </>
      );
    case "brow":
      return (
        <>
          <Eye {...L} ink={ink} />
          <Eye {...R} ink={ink} />
          <path d={`M${L.x - 3} ${L.y - 5.4}l5.4 -1.6`} stroke={ink} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "wink":
      return (
        <>
          <Eye {...L} ink={ink} />
          <Closed {...R} ink={ink} up={false} />
        </>
      );
    case "happy":
      return (
        <>
          <Closed {...L} ink={ink} />
          <Closed {...R} ink={ink} />
          <ellipse cx={L.x - 1.6} cy={L.y + 4.4} rx="2.2" ry="1.2" fill="#fff" fillOpacity="0.28" />
          <ellipse cx={R.x + 1.6} cy={R.y + 4.4} rx="2.2" ry="1.2" fill="#fff" fillOpacity="0.28" />
        </>
      );
    case "shades":
      return (
        <>
          <path
            d={`M${L.x - 4.6} ${L.y - 2.6}h9.2v2.4c0 2.2-1.6 3.6-3.9 3.6h-1.4c-2.3 0-3.9-1.4-3.9-3.6Z`}
            fill={ink}
          />
          <path
            d={`M${R.x - 4.6} ${R.y - 2.6}h9.2v2.4c0 2.2-1.6 3.6-3.9 3.6h-1.4c-2.3 0-3.9-1.4-3.9-3.6Z`}
            fill={ink}
          />
          <path d={`M${L.x + 4.6} ${L.y - 1.8}h${R.x - L.x - 9.2}`} stroke={ink} strokeWidth="1.9" />
          <path d={`M${L.x - 2.8} ${L.y - 1.2}l2 0`} stroke="#fff" strokeOpacity="0.55" strokeWidth="1.2" strokeLinecap="round" />
          <path d={`M${R.x - 2.8} ${R.y - 1.2}l2 0`} stroke="#fff" strokeOpacity="0.55" strokeWidth="1.2" strokeLinecap="round" />
        </>
      );
  }
};

export const isBlobHue = (value: string): value is BlobHue => (blobHues as readonly string[]).includes(value);
export const isBlobFace = (value: string): value is BlobFace => (blobFaces as readonly string[]).includes(value);

export interface BlobProps extends Omit<ComponentProps<"svg">, "children"> {
  readonly hue: string;
  readonly face?: string;
  /** Rendered size in px. Sidebar rows use 20, message avatars 28, headers 24, profiles 64. */
  readonly size?: number;
}

export const Blob = ({ hue, face = "plain", size = 20, className, ...props }: BlobProps) => {
  const id = useId();
  const palette = palettes[isBlobHue(hue) ? hue : "teal"];
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      aria-hidden
      shapeRendering="geometricPrecision"
      className={cn("shrink-0", className)}
      {...props}
    >
      <defs>
        <radialGradient id={`${id}b`} cx="36%" cy="30%" r="78%">
          <stop offset="0" stopColor={palette.light} />
          <stop offset="0.5" stopColor={palette.mid} />
          <stop offset="1" stopColor={palette.deep} />
        </radialGradient>
        {/* Inner shade: darkens the lower right rim so the body turns, not just fades. */}
        <radialGradient id={`${id}s`} cx="38%" cy="32%" r="72%">
          <stop offset="0.62" stopColor={palette.deep} stopOpacity="0" />
          <stop offset="1" stopColor={palette.ink} stopOpacity="0.45" />
        </radialGradient>
      </defs>
      <path d={palette.body} fill={`url(#${id}b)`} />
      <path d={palette.body} fill={`url(#${id}s)`} />
      <path d={palette.body} fill="none" stroke={palette.ink} strokeOpacity="0.35" strokeWidth="1" />
      <ellipse cx="13.2" cy="10.4" rx="4.6" ry="2.5" transform="rotate(-28 13.2 10.4)" fill="#fff" fillOpacity="0.42" />
      <circle cx="19.6" cy="7.4" r="1" fill="#fff" fillOpacity="0.35" />
      <Face face={isBlobFace(face) ? face : "plain"} ink={palette.ink} />
    </svg>
  );
};

/**
 * A group room's faces. Two members overlap on a diagonal (Paper: ui/AvatarStack); three or
 * more show the first three as a trio, each face about two thirds of the avatar with a ring in
 * the card colour between them, big enough to tell apart at sidebar size.
 */
export const BlobStack = ({
  members,
  size = 20,
}: {
  members: ReadonlyArray<{ hue: string; face?: string }>;
  size?: number;
}) => {
  const [first, second] = members;
  if (first === undefined) return <span style={{ width: size, height: size }} className="shrink-0" />;
  if (second === undefined) return <Blob hue={first.hue} face={first.face ?? "plain"} size={size} />;
  const third = members[2];
  if (third !== undefined) {
    const face = Math.round(size * 0.66);
    const edge = "absolute rounded-full ring-[1.5px] ring-(--row-bg)";
    return (
      <span className="relative shrink-0" style={{ width: size, height: size }}>
        <Blob hue={first.hue} face={first.face ?? "plain"} size={face} className={edge} style={{ left: -1, top: -1 }} />
        <Blob hue={second.hue} face={second.face ?? "plain"} size={face} className={edge} style={{ right: -1, top: -1 }} />
        <Blob
          hue={third.hue}
          face={third.face ?? "plain"}
          size={face}
          className={edge}
          style={{ left: (size - face) / 2, bottom: -2 }}
        />
      </span>
    );
  }
  const small = Math.round(size * 0.7);
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <Blob hue={first.hue} face={first.face ?? "plain"} size={small} className="absolute left-0 top-0" />
      <Blob
        hue={second.hue}
        face={second.face ?? "plain"}
        size={small}
        className="absolute bottom-0 right-0 rounded-full ring-2 ring-(--row-bg)"
      />
    </span>
  );
};

